import * as z from 'zod'
import {
  driverStatusLabels,
  driverStatusVariant,
  massMailingStatusVariant,
  Option,
  OptionEnum,
} from '~/shared/config/constants'
import { DriverStatus, MassMailingStatus } from '~/shared/config/enums'
import {
  dateTimeSchema,
  dateSchema,
  enumOptionSchema,
  uuidOptionsSchema,
} from '~/shared/lib/schemas'
import { isString } from '~/shared/lib/utils'
import { ApiModel, ToManyRelation } from './core'
import { Region } from './region'

const attributeSchema = z.object({
  name: z.string().min(1, 'Обязательное поле'),
  title: z.string().min(1, 'Обязательное поле'),
  content: z.string().min(1, 'Обязательное поле'),
  isActive: z.boolean(),
  userStatuses: enumOptionSchema(DriverStatus).array().nullable().optional(),
  status: enumOptionSchema(MassMailingStatus),
  scheduledAt: dateTimeSchema,
})

const relationsSchema = z.object({
  regionsIds: uuidOptionsSchema,
})

const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(relationsSchema)
  .merge(attributeSchema)

export type MassMailingAttributes = z.infer<typeof schema>

export class MassMailing extends ApiModel<typeof schema> {
  static jsonApiType = 'mass-mailings'

  static schema = schema

  getName(): string {
    return this.getAttribute('name')
  }

  getContent(): string {
    return this.getAttribute('content')
  }

  getIsActive(): boolean {
    return this.getAttribute('isActive')
  }

  getScheduledAt(): string | null {
    return this.getAttribute('scheduledAt')
  }

  getStatus(): MassMailingStatus {
    return this.getAttribute('status')
  }

  getUserStatuses(): DriverStatus[] {
    return this.getAttribute('userStatuses') || []
  }

  getUserStatusLabel(status: DriverStatus): string {
    return driverStatusLabels[status] || status
  }

  getUserStatusColor(status: DriverStatus): string {
    return driverStatusVariant[status] || 'gray'
  }

  getStatusVariant(status: MassMailingStatus) {
    return massMailingStatusVariant[status]
  }

  getUserStatusesOptions(): OptionEnum<DriverStatus>[] {
    const statuses = this.getUserStatuses()
    return statuses.map((status: DriverStatus) => ({
      id: status,
      label: driverStatusLabels[status],
    }))
  }

  regions(): ToManyRelation<Region, this> {
    return this.hasMany(Region)
  }

  getRegions(): Region[] {
    return this.getRelation('regions')
  }

  setRegions(ids: UniqueId[] | Option[]) {
    let regions: Region[] = []
    ids.map((regionId) => {
      const region = new Region()
      if (isString(regionId)) {
        region.setApiId(regionId)
        regions = regions.concat(region)
      }
    })
    this.setRelation('regions', regions)
  }
}
